import { UserPlus } from "lucide-react";
import { Button } from "@primitives/button";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useToast } from "@/hooks/useToast";
import { useNavigationState } from "@/zustand/NavigationState";
import { MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD } from "@/config/constants";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { usePaginatedMembersWithPrefixSearch } from "@/middleware/hooks/usePaginatedMembersWithPrefixSearch";

import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useState, useRef } from "react";
import { SEARCH_DEBOUNCE_TIME } from "@/config/constants";
import { Input } from "@/components/primitives/input";
import MemberListItem from "./MemberListItem";
import { GroupChatMember } from "@/server/types/member";
import {
  addMembersToGroupChat,
  prepareMemberForExistingGroupChat,
} from "@/middleware/members";

export default function AddMembers() {
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatECSK = useNavigationState((state) => state.activeChatECSK);

  const controllerRef = useRef<AbortController | null>(null);

  const { t } = useTranslation();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME);

  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [groupChatMembers, setGroupChatMembers] = useState<GroupChatMember[]>(
    [],
  );

  const [openDialog, setOpenDialog] = useState<true | false>(false);

  const {
    status,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    data,
    fetchNextPage,
    error,
  } = usePaginatedMembersWithPrefixSearch({
    searchTerm: debouncedSearchTerm,
    not_member: activeChatId || "", // members outside this chat
  });

  const results = data ? data.pages.flat() : [];

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  const handleSelectMember = async (
    username: string,
    newCheckedVal: boolean,
  ) => {
    if (!activeChatId || !activeChatECSK) {
      return;
    }
    if (newCheckedVal) {
      if (usernames.length === MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD) {
        toast({
          duration: 1500,
          description: t("youCanOnlyAddAMaximumNumberOfMembersPerGroupChat", {
            max: MAX_MEMBERS_IN_GROUP_CHAT_PER_PAYLOAD,
          }),
          variant: "default",
        });
        return;
      }

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      try {
        setUsernames((prev) => [...prev, username]);
        const newMember = await prepareMemberForExistingGroupChat(
          username,
          activeChatId,
          activeChatECSK,
          signal,
        );

        if (newMember) {
          setGroupChatMembers((prev) => [...prev, newMember]);
        } else {
          setUsernames((prev) => prev.filter((u) => u !== username));
        }
      } catch (isError) {
        if (isError instanceof Error && isError.message === "canceled") {
          // Request was aborted — no toast stuff
          return;
        }
        toast({
          duration: 1000,
          description:
            isError instanceof Error ? isError.message : t("errorOccurred"),
          variant: "destructive",
        });
        // remove username from the list if an error occurs
        setUsernames((prev) => prev.filter((u) => u !== username));
        // Remove the member from the list if an error occurs
        setGroupChatMembers((prev) =>
          prev.filter((member) => member.username !== username),
        );
      }
    } else {
      controllerRef.current?.abort();
      // Remove the member from the list if unchecked
      setUsernames((prev) => prev.filter((u) => u !== username));
      setGroupChatMembers((prev) =>
        prev.filter((member) => member.username !== username),
      );
    }
  };

  const handleAddMembersToGroupChat = async () => {
    if (!activeChatId) {
      return;
    }
    if (groupChatMembers.length === 0) {
      return;
    }

    try {
      setIsAddingMembers(true);

      await addMembersToGroupChat(groupChatMembers);
      toast({
        duration: 1500,
        description: `Members added successfully!`,
        variant: "default",
      });

      // we do this on ws events
      // // Invalidate the cached query results for the chat list of members
      // queryClient.invalidateQueries({
      //   queryKey: ["chat-members", activeChatId],
      // });
    } catch (isError) {
      toast({
        duration: 1000,
        description:
          isError instanceof Error ? isError.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setSearchTerm("");
      setUsernames([]);
      setGroupChatMembers([]);
      setIsAddingMembers(false);
      setOpenDialog(false);
    }
  };

  useEffect(() => {
    if (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Cannot load members",
        variant: "destructive",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();
    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= results.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNextPage,
    fetchNextPage,
    results.length,
    isFetchingNextPage,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowVirtualizer.getVirtualItems(),
  ]);

  if (!activeChatId) {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => setOpenDialog(true)}
        size="icon"
        variant="ghost"
        className="flex size-14 cursor-pointer flex-col items-center gap-1 rounded-md p-1 align-middle text-foreground hover:bg-transparent"
      >
        <UserPlus className="size-4" />
        <span className="text-sm font-normal">{t("add")}</span>
      </Button>

      <Dialog
        open={openDialog}
        onOpenChange={(isOpen) => {
          if (isAddingMembers) {
            return;
          }
          setSearchTerm("");
          setUsernames([]);
          setGroupChatMembers([]);

          setOpenDialog(isOpen);
        }}
      >
        <DialogContent className="my-auto flex h-full flex-1 flex-col border-none bg-card md:h-4/6">
          <DialogHeader>
            <DialogTitle>{t("addMembers")}</DialogTitle>
            <DialogDescription>
              {t("membersSelected", {
                count: usernames.length,
              })}
            </DialogDescription>
          </DialogHeader>
          <Input
            defaultValue={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toLocaleLowerCase())}
            type="text"
            placeholder={t("whoWouldYouLikeToAdd")}
            className="h-8 min-h-8 bg-foreground/10"
          />

          <>
            {isFetching && status === "pending" ? (
              <>
                <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                  <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                  <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                </div>
                <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                  <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                  <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                </div>
                <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                  <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                  <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                </div>
              </>
            ) : (
              <div
                ref={parentRef}
                className="custom-scroll"
                style={{
                  height: `100%`,
                  width: `100%`,
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > results.length - 1;
                    const member = results[virtualRow.index];

                    return (
                      <div
                        key={virtualRow.index}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {isLoaderRow ? (
                          hasNextPage && (
                            <>
                              <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                                <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                                <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                                <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                              </div>
                              <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                                <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                                <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                                <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                              </div>
                              <div className="flex items-center justify-start gap-3 truncate px-4 py-1 align-middle">
                                <div className="size-8 shrink-0 animate-pulse rounded-full bg-muted" />
                                <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                                <div className="size-5 shrink-0 animate-pulse rounded-full bg-muted" />
                              </div>
                            </>
                          )
                        ) : (
                          <MemberListItem
                            key={member.username}
                            username={member.username}
                            checked={usernames.includes(member.username)}
                            onChecked={handleSelectMember}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
          <DialogFooter className="mt-auto gap-2">
            <DialogClose asChild>
              <Button variant="outline" onClick={() => setOpenDialog(false)}>
                {t("cancel")}
              </Button>
            </DialogClose>

            <Button
              disabled={usernames.length === 0 || isAddingMembers}
              type="submit"
              onClick={handleAddMembersToGroupChat}
            >
              {isAddingMembers ? t("adding") : t("add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
